import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button
} from '@mui/material';
import { getSpotifyTokenFromCode } from '../utils/spotifyApi';
import api from '../utils/api';

function SpotifyCallback() {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the authorization code from the URL
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');

        if (error) {
          setStatus('error');
          setError(`Spotify authorization failed: ${error}`);
          return;
        }

        if (!code) {
          setStatus('error');
          setError('No authorization code received from Spotify');
          return;
        }

        // Exchange the code for an access token
        const redirectUri = 'http://app.localhost/spotify-callback'; // Use hardcoded URI
        
        try {
          const tokenData = await getSpotifyTokenFromCode(code, redirectUri);
          
          // If the user is authenticated, save the connection to their account
          try {
            await api.post('spotify/save-connection', {
              spotify_user_id: tokenData.spotify_user_id,
              spotify_display_name: tokenData.spotify_display_name,
              access_token: tokenData.access_token,
              refresh_token: tokenData.refresh_token,
              expires_in: tokenData.expires_in
            });
          } catch (saveError) {}
          
          setStatus('success');
          
          // Redirect back to the music selections page after a short delay
          setTimeout(() => {
            navigate('/music-selections');
          }, 2000);
        } catch (tokenError) {
          setStatus('error');
          setError('Failed to connect to Spotify. The authorization code may have expired. Please try again.');
        }
      } catch (error) {
        setStatus('error');
        setError(error.response?.data?.message || 'Failed to connect to Spotify');
      }
    };

    handleCallback();
  }, [location, navigate]);

  const handleRetry = () => {
    // Redirect to music selections page where they can try connecting again
    navigate('/music-selections');
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '100vh',
      p: 3,
      bgcolor: '#f5f5f5'
    }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          maxWidth: 500, 
          width: '100%',
          textAlign: 'center',
          borderRadius: 2
        }}
      >
        {status === 'loading' && (
          <>
            <CircularProgress size={60} sx={{ mb: 3, color: '#1DB954' }} />
            <Typography variant="h5" gutterBottom>
              Connecting to Spotify
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we complete the connection...
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Box sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              bgcolor: '#1DB954',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3
            }}>
              <Box 
                component="img" 
                src="/spotify-logo-white.png" 
                alt="Spotify" 
                sx={{ width: 50, height: 50 }}
              />
            </Box>
            <Typography variant="h5" gutterBottom>
              Successfully Connected!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your Spotify account has been connected successfully.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting you back to the music selections page...
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error || 'An error occurred while connecting to Spotify'}
            </Alert>
            <Typography variant="body1" paragraph>
              Authorization codes expire quickly. Please try connecting again.
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleRetry}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default SpotifyCallback; 